import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Paperclip, X, FileText } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'react-toastify';

const categories = [
  { value: 'BPM / Flow Ürünleri', label: 'BPM / Flow Ürünleri', color: 'bg-blue-500' },
  { value: 'Logo Tiger / Go Ürünleri', label: 'Logo Tiger / Go Ürünleri', color: 'bg-emerald-500' },
  { value: 'Bordro Ürünleri', label: 'Bordro Ürünleri', color: 'bg-purple-500' },
  { value: 'E-Devlet Ürünleri', label: 'E-Devlet Ürünleri', color: 'bg-red-500' },
  { value: 'Özel Yazılım ve Servis', label: 'Özel Yazılım ve Servis', color: 'bg-amber-500' },
];

const priorities = [
  { value: 'Düşük', label: 'Düşük', color: 'text-slate-500 border-slate-200 hover:bg-slate-50' },
  { value: 'Normal', label: 'Normal', color: 'text-blue-600 border-blue-200 hover:bg-blue-50' },
  { value: 'Yüksek', label: 'Yüksek', color: 'text-orange-600 border-orange-200 hover:bg-orange-50' },
  { value: 'Acil', label: 'Acil', color: 'text-red-600 border-red-200 hover:bg-red-50' },
];

export default function CustomerNewTicket() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: '', description: '', category: '', priority: 'Normal' });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...newFiles].slice(0, 5));
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.category) {
      toast.error('Lütfen bir kategori seçin!');
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('title', form.title);
      formData.append('description', form.description);
      formData.append('category', form.category);
      formData.append('priority', form.priority);
      files.forEach(file => formData.append('files', file));

      const res = await api.post('/tickets', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Destek talebiniz oluşturuldu!');
      navigate(`/tickets/${res.data.ticketId}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Talep oluşturulamadı');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Yeni Destek Talebi</h1>
        <p className="text-slate-500 mt-1">Sorununuzu detaylı bir şekilde açıklayın</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Category Selection */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
          <label className="block text-sm font-semibold text-slate-700 mb-3">Ürün / Kategori Seçin *</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {categories.map(cat => (
              <button key={cat.value} type="button" onClick={() => setForm({...form, category: cat.value})}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all text-left ${
                  form.category === cat.value
                    ? 'border-blue-500 bg-blue-50 shadow-sm'
                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                }`}>
                <div className={`w-3 h-3 rounded-full ${cat.color}`} />
                <span className={`text-sm font-medium ${form.category === cat.value ? 'text-blue-700' : 'text-slate-700'}`}>{cat.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Priority */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
          <label className="block text-sm font-semibold text-slate-700 mb-3">Öncelik</label>
          <div className="flex flex-wrap gap-2">
            {priorities.map(p => (
              <button key={p.value} type="button" onClick={() => setForm({...form, priority: p.value})}
                className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${p.color} ${
                  form.priority === p.value ? 'ring-2 ring-offset-1 ring-blue-400 bg-blue-50' : ''
                }`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Title & Description */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Konu *</label>
            <input type="text" value={form.title} onChange={e => setForm({...form, title: e.target.value})} required
              className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-800 placeholder-slate-400"
              placeholder="Sorununuzu kısaca özetleyin" />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Açıklama</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={6}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-800 placeholder-slate-400 resize-none"
              placeholder="Sorununuzu detaylı bir şekilde açıklayın. Hata mesajları, adımlar vb. bilgileri paylaşın." />
          </div>
        </div>

        {/* File Upload */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
          <label className="block text-sm font-semibold text-slate-700 mb-3">Dosya Ekle (max 5 dosya)</label>
          <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-blue-400 transition-colors cursor-pointer relative">
            <input type="file" multiple onChange={handleFileChange} className="absolute inset-0 opacity-0 cursor-pointer" accept="*/*" />
            <Paperclip size={24} className="text-slate-400 mx-auto mb-2" />
            <p className="text-sm text-slate-500">Dosyaları sürükleyin veya tıklayarak seçin</p>
            <p className="text-xs text-slate-400 mt-1">Max 10MB / dosya</p>
          </div>
          {files.length > 0 && (
            <div className="mt-3 space-y-2">
              {files.map((file, i) => (
                <div key={i} className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-2">
                  <div className="flex items-center gap-2">
                    <FileText size={16} className="text-blue-500" />
                    <span className="text-sm text-slate-700 truncate">{file.name}</span>
                    <span className="text-xs text-slate-400">({(file.size / 1024).toFixed(1)} KB)</span>
                  </div>
                  <button type="button" onClick={() => removeFile(i)} className="p-1 text-slate-400 hover:text-red-500"><X size={16} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Submit */}
        <button type="submit" disabled={loading}
          className="w-full py-3.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg shadow-blue-600/25 disabled:opacity-50 flex items-center justify-center gap-2 text-lg">
          {loading ? <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white" /> : <><Send size={22} /> Talebi Gönder</>}
        </button>
      </form>
    </div>
  );
}
